const RESEARCH_CONTAINER_KEY = '_builder';
const RESEARCH_PACKET_KEY = 'automaticGuideResearch';

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const getAutomaticGuideResearch = (itinerary) => (
  isObject(itinerary?.[RESEARCH_CONTAINER_KEY]?.[RESEARCH_PACKET_KEY])
    ? itinerary[RESEARCH_CONTAINER_KEY][RESEARCH_PACKET_KEY]
    : null
);

export const setAutomaticGuideResearch = (itinerary, researchPacket) => {
  const currentItinerary = isObject(itinerary) ? itinerary : {};
  const currentBuilderData = isObject(currentItinerary[RESEARCH_CONTAINER_KEY])
    ? currentItinerary[RESEARCH_CONTAINER_KEY]
    : {};

  if (!researchPacket) {
    const remainingBuilderData = { ...currentBuilderData };
    const publicItinerary = { ...currentItinerary };
    delete remainingBuilderData[RESEARCH_PACKET_KEY];
    delete publicItinerary[RESEARCH_CONTAINER_KEY];

    return Object.keys(remainingBuilderData).length > 0
      ? { ...publicItinerary, [RESEARCH_CONTAINER_KEY]: remainingBuilderData }
      : publicItinerary;
  }

  return {
    ...currentItinerary,
    [RESEARCH_CONTAINER_KEY]: {
      ...currentBuilderData,
      [RESEARCH_PACKET_KEY]: researchPacket
    }
  };
};

export const stripSourceArtifactsFromText = (value) => {
  if (typeof value !== 'string' || !value.trim()) return value || '';

  return value
    .replace(/\[([^\]]+)\]\((?:https?:\/\/|www\.)[^)]+\)/giu, '$1')
    .replace(/\b(?:https?:\/\/|www\.)[^\s<>"'`)\]}]+/giu, '')
    .replace(/\s*(?:\[\d+(?:\s*[,;\-]\s*\d+)*\]|\u3010[^\u3011]*(?:\u2020|source)[^\u3011]*\u3011)/giu, '')
    .replace(/(?:^|\n{1,2})(?:sources?|references?|bibliography)\s*:\s*[\s\S]*$/iu, '')
    .replace(/\(\s*\)/gu, '')
    .replace(/[ \t]+([,.;:!?])/gu, '$1')
    .replace(/[ \t]{2,}/gu, ' ')
    .replace(/\n{3,}/gu, '\n\n')
    .trim();
};

const sanitizePublicPoint = (point = {}) => {
  const publicPoint = { ...point };
  delete publicPoint.automaticGuideResearch;
  delete publicPoint.researchSources;
  delete publicPoint.sourceUrls;
  delete publicPoint.sources;
  delete publicPoint.factualWarnings;

  [
    'description',
    'inDepthDescription',
    'inDepthDescriptionContext',
    'historicalContext',
    'culturalSignificance',
    'hiddenGems'
  ].forEach((field) => {
    if (typeof publicPoint[field] === 'string') {
      publicPoint[field] = stripSourceArtifactsFromText(publicPoint[field]);
    }
  });

  return publicPoint;
};

export const prepareItineraryForPublication = (itinerary) => {
  if (!isObject(itinerary)) return itinerary;

  const publicItinerary = { ...itinerary };
  delete publicItinerary[RESEARCH_CONTAINER_KEY];

  const publicationItinerary = {
    ...publicItinerary,
    metadata: isObject(publicItinerary.metadata) ? { ...publicItinerary.metadata } : publicItinerary.metadata,
    points: Array.isArray(publicItinerary.points)
      ? publicItinerary.points.map(sanitizePublicPoint)
      : []
  };

  if (typeof publicationItinerary.metadata?.description === 'string') {
    publicationItinerary.metadata.description = stripSourceArtifactsFromText(
      publicationItinerary.metadata.description
    );
  }

  return publicationItinerary;
};

export const AUTOMATIC_GUIDE_RESEARCH_VERSION = 2;

const buildProjectFilename = (metadata = {}) => {
  const existingFilename = String(metadata.filename || '').replace(/\.json$/i, '');
  const fallbackName = [metadata.country, metadata.language, metadata.name]
    .filter(Boolean)
    .join('_');
  const safeBaseName = (existingFilename || fallbackName || 'guide')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  return `${safeBaseName || 'guide'}.guidebuilder.json`;
};

export const saveAutomaticGuideProjectFile = async (itinerary) => {
  const researchPacket = getAutomaticGuideResearch(itinerary);
  if (!researchPacket) {
    throw new Error('There is no automatic-guide research to save yet.');
  }

  const savedAt = new Date().toISOString();
  const projectData = {
    ...itinerary,
    _builder: {
      ...(itinerary._builder || {}),
      projectFile: {
        format: 'guidesofia-guidebuilder',
        version: 1,
        savedAt
      }
    }
  };
  const filename = buildProjectFilename(itinerary.metadata || {});
  const fileContent = JSON.stringify(projectData, null, 2);

  if ('showSaveFilePicker' in window) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{
          description: 'GuideSofia editable project',
          accept: { 'application/json': ['.json'] }
        }]
      });
      const writable = await handle.createWritable();
      await writable.write(fileContent);
      await writable.close();
      return { success: true, filename };
    } catch (error) {
      if (error.name === 'AbortError') return { success: false, cancelled: true, filename };
      console.warn('Project file picker failed; using browser download instead.', error);
    }
  }

  const blob = new Blob([fileContent], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return { success: true, filename };
};
